import React, { useState, useEffect } from "react";
import Navigation from "../components/Navigation";
import AppsShowcase from "../components/AppsShowcase";
import Seo from "../components/Seo";
import { DEFAULT_DESCRIPTION, DEFAULT_TITLE } from "../lib/seo";
import { MetaLabel, CornerTick } from "../brand/Brand";
import "./Home.css";

// ─── Hero ─────────────────────────────────────────────────────────────────────

const Hero = () => {
  const [stamp, setStamp] = useState("");

  useEffect(() => {
    const update = () => {
      const n = new Date();
      setStamp(
        `${n.getFullYear()}.${String(n.getMonth() + 1).padStart(
          2,
          "0",
        )}.${String(n.getDate()).padStart(2, "0")} / ` +
          `${String(n.getHours()).padStart(2, "0")}:${String(
            n.getMinutes(),
          ).padStart(2, "0")}:${String(n.getSeconds()).padStart(2, "0")}`,
      );
    };
    update();
    const id = setInterval(update, 1000);
    return () => clearInterval(id);
  }, []);

  return (
    <section style={{ padding: "40px 40px 80px" }} className="fw-hero-wrap">
      <div
        style={{
          maxWidth: 1440,
          margin: "0 auto",
          position: "relative",
          background: "var(--fw-cream)",
          padding: 40,
          minHeight: 760,
          backgroundImage:
            "linear-gradient(to right, rgba(17,17,16,0.04) 1px, transparent 1px)",
          backgroundSize: "40px 100%",
        }}
      >
        <CornerTick pos="tl" color="var(--fw-ink)" />
        <CornerTick pos="tr" color="var(--fw-ink)" />
        <CornerTick pos="bl" color="var(--fw-ink)" />
        <CornerTick pos="br" color="var(--fw-ink)" />

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            padding: "16px 16px 0",
          }}
          className="fw-hero-meta"
        >
          <MetaLabel>A software studio / Est. 2019 · SATX</MetaLabel>
          <MetaLabel>{stamp} CST</MetaLabel>
        </div>

        <div
          style={{ padding: "120px 16px 64px" }}
          className="fw-hero-headline-wrap"
        >
          <h1
            style={{
              fontSize: "clamp(64px, 11vw, 184px)",
              lineHeight: 0.88,
              letterSpacing: -6,
              margin: 0,
              fontWeight: 800,
              color: "var(--fw-ink)",
            }}
          >
            Software
            <br />
            that actually
            <br />
            <span style={{ fontStyle: "italic", fontWeight: 800 }}>ships</span>
            <span style={{ color: "var(--fw-red)" }}>.</span>
          </h1>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr 1fr 1fr",
            gap: 48,
            padding: "32px 16px 0",
            borderTop: "1px solid var(--fw-line)",
          }}
          className="fw-hero-sub"
        >
          <div
            style={{
              gridColumn: "span 2",
              fontSize: 20,
              lineHeight: 1.45,
              fontWeight: 500,
              color: "var(--fw-ink)",
            }}
            className="fw-hero-body"
          >
            We partner with founders, agencies, and operators to build web apps,
            mobile apps, and MVPs. One engineer. One designer. No account
            managers.
          </div>

          <div
            style={{
              display: "flex",
              alignItems: "flex-end",
              justifyContent: "flex-end",
            }}
            className="fw-hero-cta-wrap"
          >
            <a
              href="/start"
              style={{
                background: "var(--fw-red)",
                color: "var(--fw-cream)",
                padding: "18px 28px",
                textDecoration: "none",
                fontFamily: "var(--fw-mono)",
                fontSize: 13,
                letterSpacing: "1.4px",
                textTransform: "uppercase",
                fontWeight: 500,
                display: "inline-flex",
                gap: 12,
                alignItems: "center",
                transition: "background 0.15s",
              }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.background = "var(--fw-red-hover)")
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.background = "var(--fw-red)")
              }
            >
              Start a project <span>→</span>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

// ─── Services ─────────────────────────────────────────────────────────────────

const SERVICES = [
  {
    n: "01",
    t: "MVP & prototyping",
    d: "Six weeks, fixed price. Real product, real users, real code.",
    tags: ["Discovery", "Design", "Build", "Ship"],
  },
  {
    n: "02",
    t: "Web development",
    d: "Production web apps on React / Next / Node. You own the code.",
    tags: ["React", "Next.js", "Postgres", "Supabase"],
  },
  {
    n: "03",
    t: "Mobile — iOS & Android",
    d: "Native feel, one codebase. Published to both stores.",
    tags: ["React Native", "Expo", "iOS", "Android"],
  },
  {
    n: "04",
    t: "Design & UX",
    d: "Interaction design that respects the engineer who has to build it.",
    tags: ["Figma", "Prototypes", "Systems", "Audit"],
  },
  {
    n: "05",
    t: "AI integrations",
    d: "Practical LLM work — search, chat, agents — not demos.",
    tags: ["OpenAI", "Claude", "RAG", "Eval"],
  },
];

const Services = () => (
  <section
    id="services"
    style={{
      padding: "80px 40px",
      background: "var(--fw-ink)",
      color: "var(--fw-cream)",
    }}
  >
    <div style={{ maxWidth: 1440, margin: "0 auto" }}>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "280px 1fr",
          gap: 80,
          marginBottom: 48,
        }}
        className="fw-section-header"
      >
        <div>
          <MetaLabel color="var(--fw-muted-2)">Practice</MetaLabel>
          <h2
            style={{
              fontSize: 48,
              letterSpacing: -1.8,
              lineHeight: 1,
              margin: "12px 0 0",
              fontWeight: 800,
              color: "var(--fw-cream)",
            }}
          >
            What we build.
          </h2>
        </div>
        <div
          style={{
            fontSize: 18,
            lineHeight: 1.55,
            color: "var(--fw-muted-2)",
            maxWidth: 640,
            paddingTop: 28,
          }}
        >
          Five things, done well. If you need us to also handle DevOps, QA
          contractors, or ongoing support — we can, but let's talk first.
        </div>
      </div>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(5, 1fr)",
          border: "1px solid rgba(255,255,255,0.12)",
        }}
        className="fw-services-grid"
      >
        {SERVICES.map((s, i) => (
          <div
            key={i}
            style={{
              padding: 28,
              minHeight: 340,
              borderRight:
                i < SERVICES.length - 1
                  ? "1px solid rgba(255,255,255,0.12)"
                  : "none",
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
              transition: "background 0.2s",
              cursor: "default",
            }}
            onMouseEnter={(e) =>
              (e.currentTarget.style.background = "rgba(198,0,0,0.08)")
            }
            onMouseLeave={(e) =>
              (e.currentTarget.style.background = "transparent")
            }
          >
            <div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <MetaLabel color="var(--fw-muted-2)">{s.n}</MetaLabel>
                <span
                  style={{
                    color: "var(--fw-red)",
                    fontFamily: "var(--fw-mono)",
                    fontSize: 11,
                  }}
                >
                  ●
                </span>
              </div>
              <div
                style={{
                  marginTop: 20,
                  fontSize: 22,
                  fontWeight: 700,
                  letterSpacing: -0.4,
                  lineHeight: 1.1,
                }}
              >
                {s.t}
              </div>
              <div
                style={{
                  marginTop: 10,
                  fontSize: 14,
                  lineHeight: 1.5,
                  color: "var(--fw-muted-2)",
                }}
              >
                {s.d}
              </div>
            </div>
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 6,
                marginTop: 20,
              }}
            >
              {s.tags.map((t) => (
                <span
                  key={t}
                  style={{
                    fontFamily: "var(--fw-mono)",
                    fontSize: 10,
                    padding: "4px 8px",
                    border: "1px solid rgba(255,255,255,0.18)",
                    letterSpacing: 1,
                    textTransform: "uppercase",
                    color: "var(--fw-cream)",
                  }}
                >
                  {t}
                </span>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// ─── Process ──────────────────────────────────────────────────────────────────

const STEPS = [
  {
    w: "Week 0",
    t: "Intro call",
    d: "30 minutes. We listen, ask about your constraints, and tell you honestly if we're a fit.",
  },
  {
    w: "Week 1",
    t: "Scope & price",
    d: "Fixed-price proposal. If we can't scope it confidently, we do a paid discovery sprint first.",
  },
  {
    w: "Week 2–5",
    t: "Build",
    d: "Daily commits. Loom walkthroughs Fridays. Slack channel for async questions.",
  },
  {
    w: "Week 6",
    t: "Ship",
    d: "Production deploy, handover docs, 30-day warranty on bugs. You own the repo from day one.",
  },
];

const Process = () => (
  <section
    id="approach"
    style={{ padding: "80px 40px", background: "var(--fw-cream-2)" }}
  >
    <div style={{ maxWidth: 1440, margin: "0 auto" }}>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "280px 1fr",
          gap: 80,
          marginBottom: 48,
        }}
        className="fw-section-header"
      >
        <div>
          <MetaLabel>Method</MetaLabel>
          <h2
            style={{
              fontSize: 48,
              letterSpacing: -1.8,
              lineHeight: 1,
              margin: "12px 0 0",
              fontWeight: 800,
            }}
          >
            Six weeks,
            <br />
            fixed price.
          </h2>
        </div>
        <div
          style={{
            fontSize: 18,
            lineHeight: 1.55,
            color: "var(--fw-muted)",
            maxWidth: 640,
            paddingTop: 28,
          }}
        >
          Most projects follow this rhythm. Shorter if it's a prototype, longer
          if it's a multi-phase build — but the cadence is the same.
        </div>
      </div>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(4, 1fr)",
          gap: 1,
          background: "var(--fw-line-strong)",
          border: "1px solid var(--fw-line-strong)",
        }}
        className="fw-process-grid"
      >
        {STEPS.map((s, i) => (
          <div
            key={i}
            style={{
              background: "var(--fw-cream)",
              padding: 28,
              minHeight: 260,
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <MetaLabel>{s.w}</MetaLabel>
              <MetaLabel>{String(i + 1).padStart(2, "0")} / 04</MetaLabel>
            </div>
            <div
              style={{
                fontSize: 72,
                fontWeight: 800,
                letterSpacing: -3,
                lineHeight: 0.9,
                color: i === 3 ? "var(--fw-red)" : "var(--fw-ink)",
              }}
            >
              {String(i + 1).padStart(2, "0")}
            </div>
            <div>
              <div
                style={{
                  fontSize: 22,
                  fontWeight: 700,
                  letterSpacing: -0.4,
                  marginBottom: 8,
                }}
              >
                {s.t}
              </div>
              <div
                style={{
                  fontSize: 13,
                  lineHeight: 1.5,
                  color: "var(--fw-muted)",
                }}
              >
                {s.d}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// ─── Contact ──────────────────────────────────────────────────────────────────

const Contact = () => (
  <section
    id="contact"
    style={{
      padding: "120px 40px",
      background: "var(--fw-red)",
      color: "var(--fw-cream)",
    }}
  >
    <div style={{ maxWidth: 1440, margin: "0 auto", position: "relative" }}>
      <CornerTick pos="tl" color="var(--fw-cream)" />
      <CornerTick pos="tr" color="var(--fw-cream)" />
      <CornerTick pos="bl" color="var(--fw-cream)" />
      <CornerTick pos="br" color="var(--fw-cream)" />
      <div style={{ padding: "32px 16px" }}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "2fr 1fr",
            gap: 80,
            alignItems: "flex-end",
          }}
          className="fw-contact-grid"
        >
          <div>
            <MetaLabel color="rgba(243,239,232,0.75)">
              Contact / Currently booking 
            </MetaLabel>
            <div
              style={{
                fontSize: "clamp(56px, 8vw, 128px)",
                lineHeight: 0.9,
                letterSpacing: -5,
                fontWeight: 800,
                marginTop: 24,
              }}
            >
              Got a thing
              <br />
              to build?
            </div>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
            <a
              href="mailto:jm@bboycreative.com"
              style={{
                color: "var(--fw-cream)",
                textDecoration: "none",
                display: "block",
                padding: "20px 24px",
                background: "var(--fw-ink)",
                fontFamily: "var(--fw-mono)",
                fontSize: 15,
                letterSpacing: 0.5,
                transition: "opacity 0.15s",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.opacity = "0.85")}
              onMouseLeave={(e) => (e.currentTarget.style.opacity = "1")}
            >
              <div
                style={{
                  fontSize: 10,
                  opacity: 0.6,
                  letterSpacing: "1.4px",
                  textTransform: "uppercase",
                  marginBottom: 6,
                }}
              >
                Email
              </div>
              jm@bboycreative.com
            </a>
            <a
              href="/start"
              style={{
                color: "var(--fw-cream)",
                textDecoration: "none",
                display: "block",
                padding: "20px 24px",
                background: "var(--fw-ink)",
                fontFamily: "var(--fw-mono)",
                fontSize: 15,
                letterSpacing: 0.5,
                transition: "opacity 0.15s",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.opacity = "0.85")}
              onMouseLeave={(e) => (e.currentTarget.style.opacity = "1")}
            >
              <div
                style={{
                  fontSize: 10,
                  opacity: 0.6,
                  letterSpacing: "1.4px",
                  textTransform: "uppercase",
                  marginBottom: 6,
                }}
              >
                Start a project · 2-min brief
              </div>
              Begin intake →
            </a>
          </div>
        </div>
      </div>
    </div>
  </section>
);

// ─── Page ─────────────────────────────────────────────────────────────────────

const Home = () => (
  <div>
    {/* The LocalBusiness / Organization / WebSite graphs live in
        public/index.html so link scrapers that don't run JS still see them;
        this only adds the page-level head. */}
    <Seo
      title={DEFAULT_TITLE}
      description={DEFAULT_DESCRIPTION}
      path="/"
      keywords="software studio San Antonio, web app development, mobile app development, MVP development, React Native, Next.js, fixed price software"
    />
    <Navigation />
    <Hero />
    <Services />
    <AppsShowcase />
    <Process />
    <Contact />
  </div>
);

export default Home;
